import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { css } from '@bd/bdui-styles/emotion';
import { SanitizeHtml, Image } from '@bd/bdcore-utilities';
import LightBox from '@bd/bdui-lightbox';
import { colors, typography } from '../../../styles';
import ButtonSet from '../../ButtonSet';

const styles = css`
  .PageIntroBanner_wrapper {
    position: relative;
    padding: 30px 0;
    background-color: ${colors.white};
    position: relative;
    z-index: 1;
    overflow: hidden;
    @media (min-width: 992px) {
      padding: 90px 0;
    }
    &::before {
      content: '';
      display: block;
      position: absolute;
      width: 700px;
      height: 700px;
      border-radius: 50%;
      background-color: ${colors.brand6Lighter};
      left: -80px;
      top: -200px;
      z-index: 1;
    }
    &::after{
      content: '';
      display: block;
      position: absolute;
      top: 60px;
      left: 0;
      width: 55%;
      height: 42%;
      background-image: url('/images/pattern-02-light.svg');
      background-repeat: repeat;
      background-position: right top;
      z-index: 2;
      @media (min-width: 992px) {
        top: 120px;
      }
    }
  }
  .PageIntroBanner {
    position: relative;
    z-index: 3;
  }
  .PageIntroBanner_content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 0 15px;
    position: relative;
    z-index: 3;
    @media (min-width: 1200px) {
      padding: 60px 0 45px 0;
    }
  }
  .PageIntroBanner_headline_line_1{
    font-size: 21px;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1;
    color: ${colors.brand1};
    text-align: left;
    position: relative;
    margin: 0 0 10px 0;
    display: block;
    @media (min-width: 992px) {
      font-size: 32px;
    }
    @media (min-width: 1200px) {
      text-align: left;
      padding: 0;
    }
  }
  .PageIntroBanner_headline_line_2 {
    font-size: 32px;
    font-weight: 600;
    line-height: 1.2;
    font-family: ${typography.Serif};
    text-align: left;
    position: relative;
    display: block;
    @media (min-width: 992px) {
      font-size: 60px;
    }
    @media (min-width: 1200px) {
      text-align: left;
      padding: 0;
    }
    @media (min-width: 1700px) {
      padding: 0 90px 0 0;
    }
    &::after {
      content: '';
      display: block;
      margin: 30px 0;
      width: 125px;
      height: 2px;
      background-color: ${colors.brand3};
      @media (min-width: 1200px) {
        margin: 30px 0;
      }
    }
  }
  .PageIntroBanner_body {
    @media (min-width: 992px) {
      padding: 0 30px 0 0;
    }
  }
  .PageIntroBanner_media {
    width: 100%;
    height: auto;
    margin: 0 0 30px 0;
    padding: 0 15px;
    z-index: 4;
    @media (min-width: 1200px) {
      margin: 0;
    }
  }
  .PageIntroBanner_image {
    width: 100%;
    position: relative;
    z-index: 1;
  }
  .PageIntroBanner_imageOval {
    border-radius: 50%;
    width: 600px;
    height: 600px;
    position: absolute;
    right: -200px;
    top: 0;
    object-fit: cover;
  }
  .PageIntroBanner_videoWrapper {
    width: 100%;
    position: relative;
    justify-content: center;
    align-items: center;
    background-position: center center;
    background-size: cover;
    @media (min-width: 992px) {
      margin: 0;
    }
  }
  .PageIntroBanner_videoButton {
    cursor: pointer;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-image: url("/images/icon-play-button-white.svg");
    background-position: center center;
    background-size: 90px 90px;
    background-repeat: no-repeat;
    display: block;
    z-index: 2;
  }
  .PageIntroBanner_green {
    &::before {
      background-color: #C1EE4A;
      opacity: .3;
    }
    .PageIntroBanner_headline_line_1 {
      color: #278501;
    }
    .PageIntroBanner_headline_line_2 {
      &::after {
        background-color: #89D56B;
      }
    }
  }
`;

const videoCss = css`
  margin: 10vh auto 0;
  @media (min-width: 992px) {
    width: 75vw;
    margin: 10vh auto 0;
  }
`;

function PageIntroBanner({ content }) {
  return (
    <div className={styles}>
      <div className={`PageIntroBanner_wrapper ${get(content, '[0].green_accent_color') ? 'PageIntroBanner_green' : ''}`}>
        <div className="container PageIntroBanner">
          <div className="row justify-content-center">
            {
              get(content, '[0].photo.contentUrl') &&
              <div className={`order-xl-2 ${get(content, '[0]image_oval') ? 'col-xl-5' : 'col-xl-6'}`}>
                <div className="PageIntroBanner_media">
                  {
                    get(content, '[0].video_embed_code') &&
                    <div
                      className="PageIntroBanner_videoWrapper"
                      role="button"
                      tabIndex="0"
                    >
                      <Image
                        src={get(content, '[0].photo.contentUrl')}
                        width={1200}
                        height={1200}
                        className="PageIntroBanner_image"
                        alt={get(content, '[0].photo.name', get(content, '[0].photo.caption', ''))}
                      />
                      <LightBox
                        enableZoom
                        items={[
                          {
                            element: (
                              <div
                                className={`${videoCss} embed-responsive embed-responsive-16by9`}
                                dangerouslySetInnerHTML={SanitizeHtml(get(content, '[0].video_embed_code'))}
                              />
                            ),
                          },
                        ]}
                      >
                        <span className="PageIntroBanner_videoButton" />
                      </LightBox>
                    </div>
                  }
                  {
                    !get(content, '[0].video_embed_code') &&
                    <Image
                      src={get(content, '[0].photo.contentUrl')}
                      width={1200}
                      height={1200}
                      className={`PageIntroBanner_image ${get(content, '[0]image_oval') ? 'PageIntroBanner_imageOval d-none d-xl-block' : ''}`}
                      alt={get(content, '[0].photo.name', get(content, '[0].photo.caption', ''))}
                    />
                  }
                </div>
              </div>
            }
            {
              (get(content, '[0].headline_line_1') || get(content, '[0].headline_line_2') || get(content, '[0].text')) &&
              <div className={`order-xl-1 d-flex ${get(content, '[0]image_oval') ? 'col-xl-7' : 'col-xl-6'}`}>
                <div className="PageIntroBanner_content">
                  <h2 className="PageIntroBanner_heading">
                    <span className="PageIntroBanner_headline_line_1">{get(content, '[0].headline_line_1')} </span>
                    <span className="PageIntroBanner_headline_line_2">{get(content, '[0].headline_line_2')} </span>
                  </h2>
                  <div className="PageIntroBanner_body" dangerouslySetInnerHTML={SanitizeHtml(get(content, '[0].text'))} />
                  <ButtonSet buttonSetParent={get(content, '[0]')} />
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

PageIntroBanner.propTypes = {
  content: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default PageIntroBanner;



// WEBPACK FOOTER //
// ./src/components/PageLoader/Sections/PageIntroBanner.section.js